import { useEffect, useState } from 'react'
import { Scale } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useRaiseSinOffering } from '../../hooks/backend/sin'
import { writeErrorMessage } from '../../utils/writeError'
import { zar } from './sinModel'

export type SinPerson = { person_id: string; name: string; route: 'payslip' | 'invoice' | null }

/**
 * Raise a charge against a person.
 *
 * THE REASON IS REQUIRED AND THE DIALOG SAYS WHY. The database refuses a manual
 * charge with no reason on it, and the reason is not paperwork: the person
 * charged is invited to answer it in writing, and an answer to "R50" with no
 * statement of what it was for is not an answer anybody can give.
 *
 * WHAT THIS DIALOG DOES NOT DECIDE. Whether the charge lands approved or waiting
 * is read from the raiser's own capability inside sin_offering_raise_v1, so this
 * form has no approve control and offers no choice about it. Telling the person
 * here what will happen would mean this screen and the ledger each holding an
 * opinion, and the ledger's is the one that counts.
 *
 * The notice to the person charged is queued by the same database call, in the
 * same transaction. It is not a second step this dialog could forget: a charge
 * that exists without the person having been told is the failure this whole tab
 * is judged by.
 */
export function RaiseSinDialog({
  open,
  onOpenChange,
  people,
  amountZar,
  onRaised,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  people: SinPerson[]
  amountZar: number | string | null
  onRaised?: () => void
}) {
  const raise = useRaiseSinOffering()
  const [personId, setPersonId] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setPersonId('')
      setReason('')
    }
  }, [open])

  const chosen = people.find((p) => p.person_id === personId) ?? null

  async function submit() {
    if (!personId) {
      toast.error('Choose who this is against.')
      return
    }
    if (!reason.trim()) {
      toast.error('Say what the charge is for.')
      return
    }
    setBusy(true)
    try {
      await raise.trigger(
        { person_id: personId, reason_code: 'manual', reason: reason.trim() },
        { skipCache: true },
      )
      toast.success('Recorded, and the person has been told.')
      onOpenChange(false)
      onRaised?.()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Raise a Sin Offering
          </DialogTitle>
          <DialogDescription>
            {amountZar === null
              ? 'No policy is in force, so no amount is set.'
              : `${zar(amountZar)}, recorded against the person and answerable by them in writing. Nothing is deducted.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sin-person">Who</Label>
            <NativeSelect
              id="sin-person"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
            >
              <option value="">Choose a person</option>
              {people.map((p) => (
                <option key={p.person_id} value={p.person_id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect>
            {/* Said before the charge is raised, not after, because the two sides
                carry different law and the person raising it should know which
                one they are reaching for. */}
            {chosen?.route === 'invoice' && (
              <p className="text-xs text-muted-foreground">
                {chosen.name} invoices rather than earning a salary, so this would be an invoice
                deduction and would need a clause in that agreement before it could ever settle.
              </p>
            )}
            {chosen && chosen.route === null && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                It is not on record whether {chosen.name} is employed or invoices, so this will be
                refused until a staff engagement row exists.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sin-reason">What it is for</Label>
            <Textarea
              id="sin-reason"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What was not done, and what was asked for."
            />
            <p className="text-xs text-muted-foreground">
              The person is sent this wording. Write it so they can answer it.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Spinner className="mr-2 h-4 w-4" />}
            Raise it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
